import { supabase } from '@/integrations/supabase/client';
import type { CommunityPost, PostDocument } from '@/types/community';
import { awardPoints, revokePoints, awardTabBonus } from '@/services/communityGamification';
import { enrichWithProfiles } from '@/services/profileUtils';
import { enrichWithTags } from './_shared';
import { logToolUsageOncePerDay } from '@/hooks/useRequestLogger';
import { attachFile, listAreas, rpc, toOriginalPost, type CerebroPost } from './cerebro';

export async function fetchPosts(
  type: string,
  opts: { page?: number; pageSize?: number; tagSlug?: string; ascending?: boolean } = {},
): Promise<{ posts: CommunityPost[]; count: number }> {
  const { page = 1, pageSize = 20, tagSlug, ascending = false } = opts;
  void tagSlug;
  const area = (await listAreas()).find(item => item.slug === type);
  if (!area) return { posts: [], count: 0 };
  const requested = Math.min(page * pageSize, 50);
  const payload = await rpc<{ items?: CerebroPost[]; next_cursor?: unknown }>('list_community_feed', {
    p_area_id: area.id,
    p_before_pinned: null,
    p_before_created_at: null,
    p_before_id: null,
    p_limit: requested,
  });
  const all = await Promise.all((payload?.items || []).map(toOriginalPost));
  const rawPosts = all.slice((page - 1) * pageSize, page * pageSize);
  // Fire-and-forget tool-usage tracking (once per session per Brazil day)
  logToolUsageOncePerDay('community_posts');
  const posts = ascending ? [...rawPosts].reverse() : rawPosts;
  return { posts, count: payload?.next_cursor ? requested + 1 : all.length };
}

export async function fetchPostById(postId: string): Promise<CommunityPost | null> {
  try {
    const data = await rpc<CerebroPost>('get_community_post', { p_post_id: postId });
    return data ? toOriginalPost(data) : null;
  } catch {
    return null;
  }
}

export async function createPost(params: {
  post_type: string;
  tab_type?: string;
  title?: string;
  content: string;
  image_url?: string;
  link_url?: string;
  video_url?: string;
  user_id: string;
  tag_ids?: string[];
  allow_replies?: boolean;
  documents?: PostDocument[];
  image_file?: File;
  document_files?: File[];
}) {
  const area = (await listAreas()).find(item => item.slug === params.post_type);
  if (!area) throw new Error('Área da Comunidade indisponível.');
  const postId = await rpc<string>('create_community_post', {
    p_area_id: area.id,
    p_title: params.title || '',
    p_content: [params.content, params.link_url, params.video_url].filter(Boolean).join('\n\n'),
    p_mentioned_user_ids: [],
  });
  for (const file of [params.image_file, ...(params.document_files || [])].filter(Boolean) as File[]) {
    await attachFile({ postId, file });
  }
  return { id: postId };
}

export async function updatePost(
  postId: string,
  params: {
    title?: string | null;
    content?: string;
    image_url?: string | null;
    link_url?: string | null;
    video_url?: string | null;
    documents?: any;
    allow_replies?: boolean;
    image_file?: File;
    document_files?: File[];
  },
) {
  await rpc('update_community_post', {
    p_post_id: postId,
    p_title: params.title || '',
    p_content: [params.content, params.link_url, params.video_url].filter(Boolean).join('\n\n'),
  });
  for (const file of [params.image_file, ...(params.document_files || [])].filter(Boolean) as File[]) {
    await attachFile({ postId, file });
  }
}

export async function togglePostReplies(postId: string, allowReplies: boolean) {
  void postId;
  void allowReplies;
}

export async function deletePost(postId: string) {
  await rpc('delete_community_post', { p_post_id: postId });
}
